<script setup>
import { ref, toRefs, onBeforeMount } from 'vue'
import mySwal from '@/src/javascripts/plugins/mySwal.js'
import { SendOutlined } from '@ant-design/icons-vue'
import BaseRadio from '@/src/vueComponent/BaseRadio.vue'
import BaseTextInput from '@/src/vueComponent/BaseTextInput.vue'
import BaseSelect from '@/src/vueComponent/BaseSelect.vue'
import BackButton from '@/src/vueComponent/BackButton.vue'
import { hashFnv32a } from '@/src/javascripts/script.js'

const props = defineProps({
  token: {
    type: String
  },
  userInfo: {
    type: Object,
    default: () => {
      return {
        id: null,
        account: '',
        password: '',
        name: '',
        job_title: '',
        email: '',
        company: '',
        company_id: '',
        company_type: '',
        unit_id: '',
        permission_id: '',
        state: ''
      }
    }
  },
  companies: {
    type: Array
  },
  units: {
    type: Array
  },
  permissions: {
    type: Array
  }
})
const { token, userInfo, companies, units, permissions } = toRefs(props)
const editUserInfo = ref(userInfo.value)

const companyList = ref([])
const unitList = ref([])
const permissionList = ref([])
onBeforeMount(() => {
  companyList.value = companies.value.map((company) => {
    return {
      value: `${company.type}${company.id}`,
      label: company.name
    }
  })
  unitList.value = units.value.map((unit) => {
    return {
      value: unit.id,
      label: unit.name
    }
  })
  permissionList.value = permissions.value.map((permission) => {
    return {
      value: permission.id,
      label: permission.name
    }
  })

  if (editUserInfo.value.id !== null) {
    editUserInfo.value.company = `${editUserInfo.value.company_type}${editUserInfo.value.company_id}`
  } else {
    editUserInfo.value.company_type = 'Jsw'
    editUserInfo.value.company_id = 0
    editUserInfo.value.company = 'Jsw0'
  }
})

const handleCompanySelect = (companyTypeAndID) => {
  editUserInfo.value.company = companyTypeAndID

  if (companyTypeAndID.includes('Supplier')) {
    editUserInfo.value.company_type = 'Supplier'
    editUserInfo.value.company_id = companyTypeAndID.split('Supplier')[1]
    editUserInfo.value.unit_id = null
    editUserInfo.value.permission_id = 3
  } else if (companyTypeAndID.includes('Customer')) {
    editUserInfo.value.company_type = 'Customer'
    editUserInfo.value.company_id = companyTypeAndID.split('Customer')[1]
    editUserInfo.value.unit_id = null
    editUserInfo.value.permission_id = 2
  } else {
    editUserInfo.value.company_type = 'Jsw'
    editUserInfo.value.company_id = 0
    editUserInfo.value.permission_id = null
  }
}

const submitForm = () => {
  if (editUserInfo.value.password !== '' && editUserInfo.value.password !== undefined) {
    editUserInfo.value.password = hashFnv32a(editUserInfo.value.password, true)
  }

  $.ajax({
    url: editUserInfo.value.id ? `/settings/users/${editUserInfo.value.id}` : '/settings/users',
    type: editUserInfo.value.id ? 'PATCH' : 'POST',
    dataType: 'json',
    headers: { 'X-CSRF-Token': token.value },
    authenticity_token: true,
    data: editUserInfo.value,
    success: async (response) => {
      if (response.state === 0) {
        window.location.replace('/settings/users')
      }
    },
    error: async (error) => {
      console.log('error', error)

      if (error.responseJSON.state === -1) {
        await mySwal.error({
          title: error.responseJSON.message.join('<br />'),
          timer: 3000
        })

        if (error.responseJSON.redirect_to) {
          window.location.replace(error.responseJSON.redirect_to)
        }
      }
    }
  })
}
</script>
<template>
  <form @submit.prevent="submitForm" class="leave-need-confirm">
    <div class="mb-3">
      <p class="flex items-baseline gap-1 py-1 text-lg font-bold">
        <span>狀態</span>
        <span class="text-sm font-light text-red-400">*</span>
      </p>
      <div class="mb-4 flex gap-3">
        <BaseRadio
          :label="'啟用'"
          :name="'state'"
          :value="'enable'"
          v-model="editUserInfo.state"
          required
        />
        <BaseRadio
          :label="'停用'"
          :name="'state'"
          :value="'disable'"
          v-model="editUserInfo.state"
          required
        />
      </div>
      <div class="mb-4 grid grid-cols-4 gap-4 md:grid-cols-2">
        <BaseTextInput
          :label="'帳號'"
          :name="'account'"
          v-model="editUserInfo.account"
          :maxlength="20"
          :minlength="3"
          required
        />
        <div>
          <p class="flex items-center gap-1 py-1">
            <span class="text-lg font-bold">密碼</span>
            <span v-if="editUserInfo?.id === null" class="text-sm font-bold text-red-400"> * </span>
            <span v-else>
              <i class="align-middle text-gray-400"></i>
              <small class="align-middle text-gray-400">若不修改密碼，則毋須填寫此欄位 </small>
            </span>
          </p>
          <BaseTextInput
            :name="'password'"
            :type="'password'"
            v-model="editUserInfo.password"
            :minlength="4"
            :maxlength="30"
            :required="editUserInfo?.id === null || editUserInfo.password?.length > 0"
          />
        </div>
        <BaseTextInput
          :label="'名稱'"
          :name="'name'"
          v-model="editUserInfo.name"
          :maxlength="20"
          required
        />
        <BaseTextInput
          :label="'職稱'"
          :name="'job_title'"
          v-model="editUserInfo.job_title"
          :maxlength="20"
        />
        <BaseTextInput
          type="email"
          :label="'電子信箱'"
          :name="'email'"
          v-model="editUserInfo.email"
          :maxlength="50"
          required
        />
        <BaseSelect
          :label="'公司'"
          :name="'company'"
          :optionList="companyList"
          :optionsSelected="editUserInfo.company"
          @select="handleCompanySelect"
          required
        />
        <input type="hidden" name="company_id" v-model="editUserInfo.company_id" />
        <input type="hidden" name="company_type" v-model="editUserInfo.company_type" />
        <BaseSelect
          :label="'單位'"
          :name="'unit_id'"
          :optionList="unitList"
          :optionsSelected="editUserInfo.unit_id"
          @select="editUserInfo.unit_id = $event"
          :required="editUserInfo.company_type === 'Jsw'"
          :disabled="editUserInfo.company_type !== 'Jsw'"
        />
        <BaseSelect
          :label="'權限群組'"
          :name="'permission_id'"
          :optionList="permissionList"
          :optionsSelected="editUserInfo.permission_id"
          @select="editUserInfo.permission_id = $event"
          required
          :disabled="editUserInfo.company_type !== 'Jsw'"
        />
      </div>
    </div>
    <div class="flex items-center justify-end gap-3 py-3">
      <BackButton :hasForm="true" :returnPath="'/settings/users?company_type=Jsw'" />
      <button type="submit" class="btn btn-success text-xl text-white">
        <SendOutlined />
        送出
      </button>
    </div>
  </form>
</template>
